import { ref, computed } from 'vue';
import addonsAPI from 'dashboard/api/omni/addons';
import { useAlert } from 'dashboard/composables';

export function useOmniAddons() {
  const addons = ref([]);
  const activeSubscriptions = ref([]);
  const loading = ref(false);

  const availableAddons = computed(() => {
    return addons.value.filter(addon => !addon.is_subscribed);
  });

  const subscribedAddons = computed(() => {
    return addons.value.filter(addon => addon.is_subscribed);
  });

  const fetchAddons = async () => {
    loading.value = true;
    try {
      const response = await addonsAPI.getAll();
      addons.value = response.data.addons || [];

      // Update with active subscriptions if already loaded
      if (activeSubscriptions.value.length > 0) {
        updateAddonsWithSubscriptionStatus();
      }
    } catch (error) {
      useAlert(error.message || 'Erro ao carregar add-ons');
    } finally {
      loading.value = false;
    }
  };

  const fetchActiveSubscriptions = async () => {
    try {
      const response = await addonsAPI.getActive();
      activeSubscriptions.value = response.data.subscriptions || [];

      // Update is_subscribed flag in addons list if addons are already loaded
      if (addons.value.length > 0) {
        updateAddonsWithSubscriptionStatus();
      }
    } catch (error) {
      useAlert(error.message || 'Erro ao carregar assinaturas ativas');
    }
  };

  // Helper to update addons with subscription status
  // Now supports multiple subscriptions per addon
  const updateAddonsWithSubscriptionStatus = () => {
    addons.value = addons.value.map(addon => {
      // Get ALL subscriptions for this addon (can be multiple)
      const subscriptions = activeSubscriptions.value.filter(
        sub => sub.addon?.id === addon.id
      );
      // Calculate total quantity across all subscriptions
      const totalQuantity = subscriptions.reduce((sum, sub) => sum + (sub.quantity || 1), 0);
      
      return {
        ...addon,
        is_subscribed: subscriptions.length > 0,
        total_quantity: totalQuantity,
        subscriptions_count: subscriptions.length,
      };
    });
  };

  const createCheckout = async (addonId, billingCycle = 'monthly', quantity = 1) => {
    loading.value = true;
    try {
      const response = await addonsAPI.createCheckout(addonId, billingCycle, quantity);
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      useAlert(
        error.response?.data?.error || 'Erro ao criar sessão de checkout'
      );
      loading.value = false;
      throw error;
    }
  };

  // DEPRECATED: Use cancelSubscriptionById for better control with multiple subscriptions
  const cancelSubscription = async (addonId, atPeriodEnd = true) => {
    loading.value = true;
    try {
      const response = await addonsAPI.cancelSubscription(addonId, atPeriodEnd);
      useAlert(response.data.message, 'success');

      // Refresh subscriptions
      await fetchActiveSubscriptions();
    } catch (error) {
      useAlert(
        error.response?.data?.error || 'Erro ao cancelar assinatura do add-on'
      );
      throw error;
    } finally {
      loading.value = false;
    }
  };

  // Cancel a specific subscription by its ID (recommended for multiple subscriptions)
  const cancelSubscriptionById = async (subscriptionId, atPeriodEnd = true) => {
    loading.value = true;
    try {
      const response = await addonsAPI.cancelSubscriptionById(subscriptionId, atPeriodEnd);
      useAlert(response.data.message, 'success');

      // Refresh subscriptions
      await fetchActiveSubscriptions();
    } catch (error) {
      useAlert(
        error.response?.data?.error || 'Erro ao cancelar assinatura'
      );
      throw error;
    } finally {
      loading.value = false;
    }
  };

  // Get ALL subscriptions for a specific addon (returns array)
  const getSubscriptionsForAddon = addonId => {
    return activeSubscriptions.value.filter(sub => sub.addon?.id === addonId);
  };

  // Get first subscription for addon (for backward compatibility)
  const getSubscriptionForAddon = addonId => {
    return activeSubscriptions.value.find(sub => sub.addon?.id === addonId);
  };

  const isAddonSubscribed = addonId => {
    // Check both activeSubscriptions and addon.is_subscribed flag
    const inActiveSubscriptions = activeSubscriptions.value.some(sub => sub.addon?.id === addonId);
    const inAddons = addons.value.find(a => a.id === addonId)?.is_subscribed;
    return inActiveSubscriptions || inAddons;
  };

  // Check if ANY subscription for this addon is being canceled
  const isAddonCanceled = addonId => {
    const subscriptions = getSubscriptionsForAddon(addonId);
    return subscriptions.some(sub => !!(sub && sub.canceled_at));
  };

  // Get total quantity across all subscriptions for an addon
  const getTotalQuantityForAddon = addonId => {
    const subscriptions = getSubscriptionsForAddon(addonId);
    return subscriptions.reduce((sum, sub) => sum + (sub.quantity || 1), 0);
  };

  return {
    addons,
    activeSubscriptions,
    availableAddons,
    subscribedAddons,
    loading,
    fetchAddons,
    fetchActiveSubscriptions,
    createCheckout,
    cancelSubscription,
    cancelSubscriptionById,
    getSubscriptionForAddon,
    getSubscriptionsForAddon,
    isAddonSubscribed,
    isAddonCanceled,
    getTotalQuantityForAddon,
  };
}
